import React, { useState, useRef, useEffect } from 'react';
import { Search, X, MoreHorizontal } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../routes';
import TradeInPricingTabs from '../../components/TradeInPricingTabs';
import Pagination from '../../components/Pagination';
import CampaignInfoDrawer from '../../components/CampaignInfoDrawer';
import FilterDrawer from '../../components/FilterDrawer';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import apiHelper from '../../apiHelper';

// ─── Local sort header ────────────────────────────────────────────────────────
const ChevronUp = ({ color }) => (
    <svg width={12} height={12} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
        <path d="M12 8l6 6H6l6-6z" />
    </svg>
);
const ChevronDown = ({ color }) => (
    <svg width={12} height={12} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '-12px' }}>
        <path d="M12 16l-6-6h12l-6 6z" />
    </svg>
);
const PromoSortHeader = ({ label, field, sortField, sortOrder, handleSort, style = {} }) => (
    <th style={{ cursor: 'pointer', ...style }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529' }}>
            {label}
            <span onClick={() => handleSort(field)} style={{ display: 'flex', flexDirection: 'column', lineHeight: 0 }}>
                <ChevronUp  color={sortField === field && sortOrder === 'asc'  ? '#212529' : '#6C757D'} />
                <ChevronDown color={sortField === field && sortOrder === 'desc' ? '#212529' : '#6C757D'} />
            </span>
        </span>
    </th>
);

const PAGE_SIZE = 10;

// ─── Status Badge ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
    Upcoming:                  { bg: '#F8F9FA', color: '#6C757D', border: '1px solid #6C757D', noteColor: '#6C757D', label: 'Upcoming' },
    Ongoing:                   { bg: '#D1E7DD', color: '#198754', border: '1px solid #198754', noteColor: '#6C757D', label: 'Ongoing' },
    Ended:                     { bg: '#E9ECEF', color: '#212529', border: '1px solid #212529', noteColor: '#6C757D', label: 'Ended' },
    'Cancelled - In advance':  { bg: '#F8D7DA', color: '#DC3545', border: '1px solid #DC3545', noteColor: '#6C757D', label: 'Cancelled' },
    'Cancelled - On schedule': { bg: '#F8D7DA', color: '#DC3545', border: '1px solid #DC3545', noteColor: '#6C757D', label: 'Cancelled' },
};

const StatusBadge = ({ status, note }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Ended;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                fontFamily: 'Montserrat', background: cfg.bg, color: cfg.color,
                border: cfg.border, whiteSpace: 'nowrap', width: 'fit-content',
            }}>
                {cfg.label || status}
            </span>
            {note && (
                <div style={{ color: cfg.noteColor, display: 'flex', alignItems: 'center', marginTop: '8px', gap: 3 }}>
                    <div style={{ height: '6px', width: '6px', borderRadius: '50%', background: cfg.color }} />
                    <div style={{ fontSize: 11, fontFamily: 'Montserrat' }}>{note}</div>
                </div>
            )}
        </div>
    );
};

// ─── Filter Icons ─────────────────────────────────────────────────────────────
const FilterActiveIcon = () => (
    <svg style={{ margin: 0 }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <g clipPath="url(#clip0_promo_filter_test)">
            <path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" />
        </g>
        <defs>
            <clipPath id="clip0_promo_filter_test">
                <rect width="12" height="12" fill="white" transform="translate(11 5)" />
            </clipPath>
        </defs>
    </svg>
);

const FilterInactiveIcon = ({ color = '#6C757D' }) => (
    <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ─── Module-level cache helpers ───────────────────────────────────────────────
function buildCacheKey(page, search, filter, sf, so) {
    return JSON.stringify({
        page,
        search: (search || '').trim(),
        filter: filter ? {
            type:  (filter.type || []).slice().sort(),
            start: filter.changesTime_start ? new Date(filter.changesTime_start).toISOString() : null,
            end:   filter.changesTime_end   ? new Date(filter.changesTime_end).toISOString()   : null,
        } : null,
        sf: sf || null,
        so,
    });
}

function buildParams(page, search, filter, sf, so) {
    const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
    const s = (search || '').trim();
    if (s) params.append('search', s);
    if (filter?.type?.length)      params.append('status',      filter.type.join(','));
    if (filter?.changesTime_start) params.append('promo_start', new Date(filter.changesTime_start).toISOString());
    if (filter?.changesTime_end)   params.append('promo_end',   new Date(filter.changesTime_end).toISOString());
    if (sf) {
        const FIELD_MAP = {
            name: 'campaign_name', status: 'campaign_status',
            itemQty: 'item_qty', promoStart: 'configured_start_at', lastModified: 'modified_at',
        };
        const dbField = FIELD_MAP[sf] || 'created_at';
        params.append('ordering', `${so === 'desc' ? '-' : ''}${dbField}`);
    }
    return params.toString();
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function PromotionalCampaignTest() {
    const history = useHistory();

    // ── Data state ──
    const [pageData,   setPageData]   = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading,  setIsLoading]  = useState(true);

    // ── Search ──
    const [searchInput,     setSearchInput]     = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // ── Filter ──
    const [appliedFilter,    setAppliedFilter]    = useState(null);
    const [filterPopupOpen,  setFilterPopupOpen]  = useState(false);
    const [filterTempValues, setFilterTempValues] = useState({
        type: [],
        changesTime_start: null,
        changesTime_end:   null,
    });

    const STATUS_TYPE_OPTIONS = [
        { id: 'Upcoming',                  title: 'Upcoming' },
        { id: 'Ongoing',                   title: 'Ongoing' },
        { id: 'Cancelled - In advance',    title: 'Cancelled - In advance' },
        { id: 'Cancelled - On schedule',   title: 'Cancelled - On schedule' },
        { id: 'Ended',                     title: 'Ended' },
    ];

    const promoFilterSections = [
        {
            label: 'Status',
            type: 'list',
            key: 'type',
            data: STATUS_TYPE_OPTIONS,
            renderItem: (item) => ({ id: item.id, title: item.title }),
        },
        {
            label: 'Promotional Period',
            type: 'dateRangeWithTime',
            key: 'changesTime',
        },
    ];

    // ── Sort ──
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');

    // ── Pagination ──
    const [currentPage, setCurrentPage] = useState(1);

    // ── Prefetch cache refs ──
    const prefetchCache      = useRef(new Map());
    const prefetchInProgress = useRef(new Set());
    const isFirstRender      = useRef(true);

    // ── Drawer / Dropdown / Modals ──
    const [drawerRow,        setDrawerRow]        = useState(null);
    const [dropdownOpenId,   setDropdownOpenId]   = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
    const dropdownRef = useRef(null);
    const buttonRefs  = useRef({});
    const [cancelConfirmRow, setCancelConfirmRow] = useState(null);
    const [endConfirmRow,    setEndConfirmRow]    = useState(null);

    const isInitialTableEmpty = !isLoading && totalCount === 0 && !debouncedSearch && !appliedFilter;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rangeEnd   = Math.min(currentPage * PAGE_SIZE, totalCount);

    // ── Sort handler ──
    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    };

    // ── Debounce search ──
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // ── Clear cache on filter/sort change ──
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }
        prefetchCache.current.clear();
        prefetchInProgress.current.clear();
    }, [debouncedSearch, appliedFilter, sortField, sortOrder]);

    // ── Prefetch helper ──
    const prefetchPage = async (page, search, filter, sf, so, totalCnt) => {
        const totalPgs = Math.ceil(totalCnt / PAGE_SIZE);
        if (page > totalPgs) return;
        const key = buildCacheKey(page, search, filter, sf, so);
        if (prefetchCache.current.has(key) || prefetchInProgress.current.has(key)) return;
        prefetchInProgress.current.add(key);
        try {
            const res = await apiHelper(`sales/promotional-campaigns/?${buildParams(page, search, filter, sf, so)}`);
            if (res?.ok) {
                const data = await res.json();
                prefetchCache.current.set(key, { results: data.results ?? [], count: data.count ?? 0 });
            }
        } catch (_) {}
        finally { prefetchInProgress.current.delete(key); }
    };

    // ── Cache-aware fetch ──
    const fetchPage = async (page, search, filter, sf, so) => {
        const key    = buildCacheKey(page, search, filter, sf, so);
        const cached = prefetchCache.current.get(key);
        if (cached) {
            setPageData(cached.results);
            setTotalCount(cached.count);
            prefetchPage(page + 1, search, filter, sf, so, cached.count);
            return;
        }
        setIsLoading(true);
        try {
            const res = await apiHelper(`sales/promotional-campaigns/?${buildParams(page, search, filter, sf, so)}`);
            if (res?.ok) {
                const data    = await res.json();
                const results = data.results ?? [];
                const count   = data.count   ?? 0;
                prefetchCache.current.set(key, { results, count });
                setPageData(results);
                setTotalCount(count);
                prefetchPage(page + 1, search, filter, sf, so, count);
            } else {
                setPageData([]); setTotalCount(0);
            }
        } catch (_) {
            setPageData([]); setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Reactive fetch ──
    useEffect(() => {
        fetchPage(currentPage, debouncedSearch, appliedFilter, sortField, sortOrder);
    }, [currentPage, debouncedSearch, appliedFilter, sortField, sortOrder]); // eslint-disable-line

    // ── Dropdown open/close ──
    const toggleDropdown = (id, e) => {
        e.stopPropagation();
        if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
        const btn = buttonRefs.current[id];
        if (btn) {
            const rect = btn.getBoundingClientRect();
            setDropdownPosition({ top: rect.bottom + window.scrollY + 4, right: window.innerWidth - rect.right });
        }
        setDropdownOpenId(id);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null);
        };
        if (dropdownOpenId !== null) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpenId]);

    useEffect(() => {
        const handleScroll = () => setDropdownOpenId(null);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, []);

    const currentRow = pageData.find(r => r.id === dropdownOpenId);

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="company-body new-look stock-wrap bg-checkbox">
            <TradeInPricingTabs />

            <div className="employee-tabs-container">
                <div className="tab-content">
                    <div className="info-section">
                        <div className="info-card" style={{ overflow: 'unset' }}>
                            <div className="entities-tab">

                                {/* ── Toolbar ── */}
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>

                                        {/* Search Box */}
                                        <div
                                            className="search-box"
                                            style={{
                                                width: '300px',
                                                position: 'relative',
                                                border: isInitialTableEmpty ? '1px solid var(--Gray-400, #CED4DA)' : '1px solid var(--Gray-600, #6C757D)',
                                                background: isInitialTableEmpty ? 'var(--Gray-100, #F8F9FA)' : 'var(--Default-White, #FFF)',
                                                pointerEvents: isInitialTableEmpty ? 'none' : 'auto',
                                            }}
                                        >
                                            <Search size={20} color={isInitialTableEmpty ? '#CED4DA' : '#6C757D'} />
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                disabled={isInitialTableEmpty}
                                                value={searchInput}
                                                onChange={e => setSearchInput(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        setDebouncedSearch(searchInput);
                                                        setCurrentPage(1);
                                                    }
                                                }}
                                            />
                                            {searchInput && (
                                                <button
                                                    onClick={() => { setSearchInput(''); setDebouncedSearch(''); setCurrentPage(1); }}
                                                    style={{
                                                        position: 'absolute', right: '4px',
                                                        background: 'transparent', border: 'none',
                                                        cursor: 'pointer', display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center', padding: '4px',
                                                    }}
                                                >
                                                    <X size={16} />
                                                </button>
                                            )}
                                        </div>

                                        {/* Filter Button */}
                                        <button
                                            className={`icon-btn p-2 filter-btn ${appliedFilter ? 'applied' : ''}`}
                                            onClick={() => { if (!isInitialTableEmpty) setFilterPopupOpen(prev => !prev); }}
                                            disabled={isInitialTableEmpty}
                                            style={{
                                                border: isInitialTableEmpty
                                                    ? '1px solid var(--Gray-400, #CED4DA)'
                                                    : appliedFilter ? '1px solid #2C7BE5' : '1px solid var(--Gray-600, #6C757D)',
                                                color: isInitialTableEmpty ? '#CED4DA' : appliedFilter ? '#2C7BE5' : '#6C757D',
                                                background: isInitialTableEmpty
                                                    ? 'var(--Gray-100, #F8F9FA)'
                                                    : appliedFilter ? '#CFE2FF' : 'var(--Default-White, #FFF)',
                                                height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                pointerEvents: isInitialTableEmpty ? 'none' : 'auto',
                                                cursor: isInitialTableEmpty ? 'not-allowed' : 'pointer',
                                                borderRadius: '8px',
                                            }}
                                        >
                                            {appliedFilter && !isInitialTableEmpty
                                                ? <FilterActiveIcon />
                                                : <FilterInactiveIcon color={isInitialTableEmpty ? '#CED4DA' : '#6C757D'} />
                                            }
                                        </button>

                                        {/* Filter Drawer */}
                                        <FilterDrawer
                                            isOpen={filterPopupOpen}
                                            onClose={() => setFilterPopupOpen(false)}
                                            sections={promoFilterSections}
                                            tempValues={filterTempValues}
                                            setTempValues={setFilterTempValues}
                                            onApply={() => {
                                                const hasType = filterTempValues.type?.length > 0;
                                                const hasTime = filterTempValues.changesTime_start || filterTempValues.changesTime_end;
                                                setAppliedFilter(hasType || hasTime ? {
                                                    type:             filterTempValues.type,
                                                    changesTime_start: filterTempValues.changesTime_start,
                                                    changesTime_end:   filterTempValues.changesTime_end,
                                                } : null);
                                                setCurrentPage(1);
                                                setFilterPopupOpen(false);
                                            }}
                                            onClear={() => {
                                                setFilterTempValues({ type: [], changesTime_start: null, changesTime_end: null });
                                                setAppliedFilter(null);
                                            }}
                                            width={400}
                                        />
                                    </div>
                                </div>

                                {/* ── Table ── */}
                                <div className="table-wrapper" style={{ border: 'none' }}>
                                    {isLoading ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 16, fontFamily: 'Montserrat' }}>
                                            <div style={{ width: 36, height: 36, border: '4px solid #f3f3f3', borderTop: '4px solid #FFEB00', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>Loading campaigns...</span>
                                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                                        </div>
                                    ) : isInitialTableEmpty ? (
                                        <div className="empty-state">
                                            <div className="empty-state-content">
                                                <div className="icon">
                                                    <img src={noBrandImage} alt="empty" style={{ width: 120, height: 120 }} />
                                                </div>
                                                <h1 className="no-item-title mt-2">No Promotional Campaign Found</h1>
                                                <p className="no-item-text">Nothing here yet. Initiate Promotional Pricing to get started!</p>
                                            </div>
                                        </div>
                                    ) : !isLoading && totalCount === 0 ? (
                                        <div className="empty-state">
                                            <div className="empty-state-content">
                                                <div className="icon">
                                                    <img src={emptySearchImg} alt="no results" style={{ width: 120, height: 120 }} />
                                                </div>
                                                <h1 className="no-item-title mt-2">No Results Found</h1>
                                                <p className="no-item-text">No results match what you're looking for</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <table className="entities-table" style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat', fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ borderBottom: '1px solid #DEE2E6' }}>
                                                    <PromoSortHeader label="Campaign Name"      field="name"         sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} style={{ padding: '10px', textAlign: 'left' }} />
                                                    <PromoSortHeader label="Status"             field="status"       sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} style={{ padding: '10px', textAlign: 'left' }} />
                                                    <PromoSortHeader label="Item Qty"           field="itemQty"      sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} style={{ padding: '10px', textAlign: 'left' }} />
                                                    <PromoSortHeader label="Promotional Period" field="promoStart"   sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} style={{ padding: '10px', textAlign: 'left' }} />
                                                    <PromoSortHeader label="Last Modified Time" field="lastModified" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} style={{ padding: '10px', textAlign: 'left' }} />
                                                    <th style={{ padding: '10px', textAlign: 'right', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pageData.map((row, idx) => (
                                                    <tr
                                                        key={row.id}
                                                        onClick={() => setDrawerRow(row)}
                                                        style={{
                                                            borderBottom: '1px solid #F2F2F2',
                                                            background: idx % 2 === 0 ? '#FFF' : '#FAFAFA',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        <td style={{ padding: '10px', fontWeight: 400, color: '#212529' }}>{row.name}</td>
                                                        <td style={{ padding: '10px' }}>
                                                            <StatusBadge status={row.status} note={row.statusNote} />
                                                        </td>
                                                        <td style={{ padding: '10px', color: '#212529' }}>{row.itemQty}</td>
                                                        <td style={{ padding: '10px', color: '#212529', whiteSpace: 'nowrap' }}>
                                                            {row.promoStart} - {row.promoEnd}
                                                        </td>
                                                        <td style={{ padding: '10px', color: '#212529', whiteSpace: 'nowrap' }}>{row.lastModified}</td>
                                                        <td style={{ padding: '10px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                                                            <button
                                                                ref={el => buttonRefs.current[row.id] = el}
                                                                onClick={e => toggleDropdown(row.id, e)}
                                                                style={{
                                                                    background: 'transparent', border: 'none', cursor: 'pointer',
                                                                    padding: '4px 8px', borderRadius: 6, color: '#6C757D',
                                                                    display: 'inline-flex', alignItems: 'center',
                                                                }}
                                                            >
                                                                <MoreHorizontal size={18} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* ── Pagination ── */}
                                {totalCount > 0 && (
                                    <div className="pagination">
                                        <div className="pagination-info">
                                            {`${rangeStart} - ${rangeEnd} of ${totalCount}`}
                                        </div>
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={setCurrentPage}
                                        />
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Action Dropdown ── */}
            {dropdownOpenId !== null && currentRow && (
                <div
                    ref={dropdownRef}
                    className="backdrop-cls"
                    style={{
                        position: 'fixed', top: dropdownPosition.top, right: dropdownPosition.right,
                        background: 'white', boxShadow: '0px 1px 4px 0px #0000001A',
                        zIndex: 1000, minWidth: 160, borderRadius: 8,
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="btn-align-forall">
                        <div className="common-for-all">
                            <button onClick={() => { setDrawerRow(currentRow); setDropdownOpenId(null); }}>View Details</button>
                        </div>
                        {(currentRow.status === 'Upcoming' || currentRow.status === 'Ongoing') && (
                            <div className="common-for-all">
                                <button onClick={() => { setDropdownOpenId(null); history.push(Routes.TradeInPricingEditCampaign.path, { campaign: currentRow }); }}>Edit</button>
                            </div>
                        )}
                        {currentRow.status === 'Upcoming' && (
                            <div className="common-for-all">
                                <button className="deactivate-cls" onClick={() => { setCancelConfirmRow(currentRow); setDropdownOpenId(null); }}>Cancel</button>
                            </div>
                        )}
                        {currentRow.status === 'Ongoing' && (
                            <div className="common-for-all">
                                <button className="deactivate-cls" onClick={() => { setEndConfirmRow(currentRow); setDropdownOpenId(null); }}>End</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Cancel Modal ── */}
            {cancelConfirmRow && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setCancelConfirmRow(null)}>
                    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', width: '650px', padding: '20px 25px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'end', height: '18px' }}>
                            <button onClick={() => setCancelConfirmRow(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'black', display: 'flex', alignItems: 'center', padding: '0 0 20px 0' }}><X size={18} /></button>
                        </div>
                        <h5 style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 20, color: '#212529', marginBottom: 10 }}>Confirm to cancel this Campaign?</h5>
                        <p style={{ fontFamily: 'Montserrat', fontSize: 14, color: '#6C757D', marginBottom: 20, lineHeight: '1.6' }}>Once cancelled, this Promotional Campaign will be discarded and will not take effect as scheduled. This action cannot be undone.</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button onClick={() => setCancelConfirmRow(null)} style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12, width: '100px', height: '34px', padding: '10px', borderRadius: 8, border: '1px solid #212529', background: '#fff', color: '#212529', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={() => setCancelConfirmRow(null)} style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12, width: '100px', height: '34px', padding: '10px', borderRadius: 8, border: 'none', background: '#212529', color: '#fff', cursor: 'pointer' }}>Discard</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── End Modal ── */}
            {endConfirmRow && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setEndConfirmRow(null)}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: '20px 25px', width: 650, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'end' }}>
                            <button onClick={() => setEndConfirmRow(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'black', display: 'flex', alignItems: 'center', padding: '0 0 20px 0' }}><X size={18} /></button>
                        </div>
                        <h5 style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 20, color: '#212529', marginBottom: 10 }}>Confirm to end this Campaign in advance?</h5>
                        <p style={{ fontFamily: 'Montserrat', fontSize: 12, color: '#6C757D', marginBottom: 20, lineHeight: '1.6' }}>Once ended, this Promotional Campaign will stop immediately and be synced to downstream platforms. This action cannot be undone.</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button onClick={() => setEndConfirmRow(null)} style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12, height: '34px', width: '100px', padding: '10px', borderRadius: 8, border: '1px solid #212529', background: '#fff', color: '#212529', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={() => setEndConfirmRow(null)} style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12, height: '34px', width: '100px', padding: '10px', borderRadius: 8, border: 'none', background: '#212529', color: '#fff', cursor: 'pointer' }}>Discard</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Campaign Info Drawer ── */}
            {drawerRow && (
                <CampaignInfoDrawer
                    row={drawerRow}
                    onClose={() => setDrawerRow(null)}
                    onEdit={(row) => { setDrawerRow(null); history.push(Routes.TradeInPricingEditCampaign.path, { campaign: row }); }}
                />
            )}

            <style jsx>{`
                .search-box { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border: 1px solid #DEE2E6; border-radius: 8px; background: white; }
                .search-box input { border: none; outline: none; width: 100%; font-size: 14px; background: transparent; }
            `}</style>
        </div>
    );
}
